import {FC, useState} from 'react';
import Button from 'components/Button';
import IconColor from 'components/IconColor';
import Information from 'components/Information';
import Spinner from 'components/Loaders/Spinner';
import Modal from 'components/Modal';
import PDFReader from 'components/PdfReader';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';
import {
    PartnerSendingOrderPreviewType,
    SendingOrderItemType,
} from 'types/partner';
import usePartnerSendingOrderPreview from './services';

type PdfItemProps = {
    label: string;
    onClick: () => void;
    selected?: boolean;
};
const PdfItem: FC<PdfItemProps> = ({label, onClick, selected}) => (
    <Button
        className={twMerge(
            'flex gap-1 h-fit py-2',
            'border-none rounded-none',
            selected
                ? 'bg-green-100 hover:bg-green-50 text-black'
                : 'bg-white hover:bg-grey-100 text-black',
        )}
        kind="custom"
        onClick={onClick}
    >
        <IconColor className="mt-1" icon="documents" size="base" />
        <span className="text-left text-wrap">{label}</span>
    </Button>
);

type PdfSelectorProps = PartnerSendingOrderPreviewType & {
    currentSelection?: number;
    onSelect: (file: SendingOrderItemType) => void;
};

const PdfSelector: FC<PdfSelectorProps> = ({
    currentSelection,
    items,
    numPages,
    onSelect,
    recipientContactOrganizationName,
}) => {
    const fileCount = items.length;

    return (
        <div className="flex min-w-80 flex-col gap-2">
            <div className="pl-4">
                <div>{t`Sending Order for: ${recipientContactOrganizationName}`}</div>
                <div>{t`File count: ${fileCount}`}</div>
                <div>{t`Page count: ${numPages}`}</div>
            </div>
            <div className="flex flex-col flex-1 border border-grey-300 bg-grey-200">
                {items.map((file, index) => (
                    <PdfItem
                        key={file.mid}
                        label={file.filename}
                        onClick={() => onSelect(file)}
                        selected={currentSelection === index}
                    />
                ))}
            </div>
        </div>
    );
};

type ModalPdfReaderProps = {
    isOpen: boolean;
    onClose: () => void;
    partnerMid: string;
    supersendMid: string;
};

/**
 * This is a reusable component that is used on a few Hats pages.
 * It is a modal that renders a collection of document pdf previews.
 */
const PdfReaderPreviewFiles: FC<ModalPdfReaderProps> = ({
    isOpen,
    onClose,
    partnerMid,
    supersendMid,
}) => {
    const {data, isLoading} = usePartnerSendingOrderPreview(
        partnerMid,
        supersendMid,
    );
    const [pdfIndex, setPdfIndex] = useState<number>(0);
    if (!isOpen) return null;

    const pdfToRender = data?.items[pdfIndex];

    let mainContent: JSX.Element;

    if (isLoading) {
        mainContent = (
            <Modal.Body className="flex justify-center">
                <Spinner />
            </Modal.Body>
        );
    } else if (data) {
        mainContent = (
            <Modal.Body className="flex h-screen max-h-[80vh] px-0">
                <PdfSelector
                    {...data}
                    currentSelection={pdfIndex}
                    onSelect={(file) => {
                        setPdfIndex(data.items.indexOf(file));
                    }}
                />
                {pdfToRender && (
                    <PDFReader
                        className="rounded-none"
                        file={pdfToRender.previewUrl}
                        showAllPages={true}
                    />
                )}
            </Modal.Body>
        );
    } else {
        mainContent = (
            <Modal.Body>
                <Information type="alert">
                    {t`An unexpected error occurred.`}
                </Information>
            </Modal.Body>
        );
    }

    return (
        <Modal
            className="w-full max-w-[80vw]"
            isOpen={isOpen}
            onClose={onClose}
        >
            <Modal.Header>{t`Preview Files`}</Modal.Header>
            {mainContent}
            <Modal.Footer>
                <Button kind="secondary" onClick={onClose} size="sm">
                    {t`Close`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PdfReaderPreviewFiles;
